import React from 'react';
import { 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  OutlinedInput, 
  Box, 
  Chip 
} from '@mui/material';
import { CLIENT_PARTY_TYPES, getClientPartyTypeName } from '../../../constants/clientPartyTypes';
import { CLIENT_PARTY_TYPE_RETURN_TYPES } from '../../../constants/selectorReturnTypes';

// Legacy export for backward compatibility
export const RETURN_VALUE_TYPES = CLIENT_PARTY_TYPE_RETURN_TYPES;

const ClientPartyTypeSelector = ({ 
  multiple = false,
  value = multiple ? [] : '', 
  onChange, 
  label = "Client Type",
  required = false,
  disabled = false,
  fullWidth = true,
  returnValue = CLIENT_PARTY_TYPE_RETURN_TYPES.ID, // CLIENT_PARTY_TYPE_RETURN_TYPES.ID or CLIENT_PARTY_TYPE_RETURN_TYPES.NAME
  size = 'medium',
  showEmptyOption = true,
  emptyOptionLabel = "None",
  ...props 
}) => {
  const clientPartyTypeOptions = Object.entries(CLIENT_PARTY_TYPES).map(([key, label]) => ({
    id: key,
    name: getClientPartyTypeName(label)
  }));

  const handleChange = (event) => {
    const selectedValue = event.target.value;
    
    if (onChange) {
      if (multiple) {
        if (returnValue === CLIENT_PARTY_TYPE_RETURN_TYPES.ID) {
          const selectedNames = selectedValue.map(id => {
            const option = clientPartyTypeOptions.find(opt => opt.id === id);
            return option?.name || '';
          }).filter(Boolean);
          onChange(selectedNames);
        } else {
          onChange(selectedValue);
        }
      } else {
        if (returnValue === CLIENT_PARTY_TYPE_RETURN_TYPES.ID) {
          const option = clientPartyTypeOptions.find(opt => opt.id === selectedValue);
          onChange(option?.name || '');
        } else {
          onChange(selectedValue || '');
        }
      }
    }
  };

  // Find the selected client type(s) for internal Select component usage
  const selectedValue = (() => {
    if (!value) return multiple ? [] : '';
    
    if (multiple) {
      if (returnValue === CLIENT_PARTY_TYPE_RETURN_TYPES.ID) {
        return value.map(name => {
          const option = clientPartyTypeOptions.find(opt => opt.name === name);
          return option?.id;
        }).filter(Boolean);
      } else {
        return Array.isArray(value) ? value : [];
      }
    } else {
      if (returnValue === CLIENT_PARTY_TYPE_RETURN_TYPES.ID) {
        const option = clientPartyTypeOptions.find(opt => opt.name === value);
        return option?.id || '';
      } else {
        return value;
      }
    }
  })();

  const renderValue = (selected) => {
    if (multiple) {
      if (!selected || selected.length === 0) {
        return <em>{emptyOptionLabel}</em>;
      }
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {selected.map((value) => {
            const option = clientPartyTypeOptions.find(opt => opt.id === value);
            return (
              <Chip 
                key={value} 
                label={option?.name} 
                size="small"
              />
            );
          })}
        </Box>
      );
    }
    
    // Handle single select rendering
    if (!selected) return '';
    const option = clientPartyTypeOptions.find(opt => opt.id === selected);
    return option?.name || selected;
  };

  return (
    <FormControl fullWidth={fullWidth} disabled={disabled} size={size} {...props}>
      <InputLabel>{label}</InputLabel>
      <Select
        multiple={multiple}
        value={selectedValue}
        onChange={handleChange}
        label={label}
        disabled={disabled}
        input={multiple ? <OutlinedInput label={label} /> : undefined}
        renderValue={renderValue}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 300,
              zIndex: 1350 // Ensure dropdown appears above table
            }
          }
        }}
      >
        {!required && !multiple && showEmptyOption && (
          <MenuItem value="">
            <em>{emptyOptionLabel}</em>
          </MenuItem>
        )}
        {clientPartyTypeOptions.map(option => (
          <MenuItem key={option.id} value={option.id}>
            {option.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default ClientPartyTypeSelector;